import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { getProjectOwnerDashboardAPI } from '@/services/apis/dashboard';
import { useRequest } from "ahooks"
import { Spin } from "antd"
import { formatAmount } from '@/utils'
import Decimal from 'decimal.js';
 
// 仪表盘组件接口定义
interface GaugeProps {
    title: string;
    isPercent?: boolean;
    dataIndex?: keyof API.Project.DashboardProjectOverview;
    subItems: Array<{
        label: string;
        value?: number;
        unit?: string;
        isMoney?: boolean;
        dataIndex: keyof API.Project.DashboardProjectOverview;
    }>;
    color: 'green' | 'blue' | 'yellow' | 'purple' | 'orange' | 'pink';
    value?: number;
}

// 仪表盘数据
const gaugeCols: GaugeProps[] = [
    {
        title: '项目周期',
        value: 0,
        isPercent: true,
        dataIndex: "projectCycle",
        color: 'green',
        subItems: [
            { label: '计划周期', unit: '天', dataIndex: "planCycle" },
            { label: '实际投入', unit: '天', dataIndex: "actualCycle" },
        ]
    },
    {
        title: '项目进度',
        value: 0,
        isPercent: true,
        dataIndex: "projectProgress",
        color: 'blue',
        subItems: [
            { label: '策划产值', isMoney: true, dataIndex: "planOutputValue" },     
            { label: '完成产值', isMoney: true, dataIndex: "completionOutputValue" },
            { label: '期间完成产值', isMoney: true, dataIndex: "periodCompletionOutputValue" },
            { label: '折算年化人均产值', isMoney: true, dataIndex: "annualized" },
        ]
    },
    {
        title: '直接成本进度',
        value: 0,
        isPercent: true,
        dataIndex: "directCostProgress",
        color: 'yellow',
        subItems: [
            { label: '直接成本预算', isMoney: true, dataIndex: "directCostBudget" },
            { label: '人力成本', isMoney: true, dataIndex: "manHourLaborCost" },
            { label: '项目成本', isMoney: true, dataIndex: "projectCost" },
            { label: '成本控制绩效指数', dataIndex: "costControlPerformanceIndex" },
        ]
    },
    {
        title: '开票进度',
        value: 0,
        isPercent: true,
        dataIndex: "invoiceProgress",
        color: 'green',
        subItems: [

        ]
    },
    {
        title: '收款进度',
        value: 0,
        isPercent: true,
        dataIndex: "collectionProgress",
        color: 'orange',
        subItems: [
            { label: '实收产值', isMoney: true, dataIndex: "actualIncome" },
        ]
    },
    {
        title: '实收毛利',
        value: 0,
        dataIndex: "actualProfitRate",
        isPercent: true,
        color: 'pink',
        subItems: [
            { label: '实收毛利', isMoney: true, dataIndex: "actualProfit" },
            { label: '实收毛利率', dataIndex: "actualProfitRate", unit: '%' },
        ]
    }, {
        title: '期间完成产值毛利率',
        value: 0,
        dataIndex: "periodCompletionOutputValueProfitRate",
        isPercent: true,
        color: 'orange',
        subItems: [
            { label: '期间完成产值毛利', dataIndex: "periodCompletionOutputValueProfit", isMoney: true },
            { label: '直接成本率', dataIndex: "directCostRate", unit: '%' },
            { label: '公司费用分摊', dataIndex: "companyExpenseAllocation", isMoney: true },
            { label: '部门成本分摊', dataIndex: "deptCostAllocation", isMoney: true },
        ]
    },
    {
        title: '高低级别人才配比',
        value: 0,
        dataIndex: "highLevelTalentRatio",
        color: 'purple',
        subItems: [
            { label: '参与人数', dataIndex: "participantCount" },
            { label: '参与人天', dataIndex: "participantDays" },
            { label: '项目饱和度', dataIndex: "projectSaturation", unit: '%' },
        ]
    }
];

// 主要组件
const ProjectManagementDashboard = ({ projectID, departs }: { projectID: number | string, departs: string[] }) => {
    
    const [renderProjectOverview, setRenderProjectOverview] = useState<GaugeProps[]>([]);
    
    const { data, loading, run: getProjectOwnerDashboard } = useRequest(getProjectOwnerDashboardAPI, {
        manual: true,
        onSuccess: (res) => {
            if (!res) return
            res.projectCycle = Number((res.actualCycle / res.planCycle).toFixed(1));
            if (res.invoiceProgress < res.collectionProgress) {
                res.invoiceProgress = res.collectionProgress
            }
            const gaugeData = gaugeCols.map(v => {
                if (v.dataIndex) {
                    v.value = Number(res[v.dataIndex]) || 0
                }
                v.subItems.forEach(item => {
                    let value = Number(res[item.dataIndex]) || 0
                    if(item.isMoney) {
                        value = formatAmount(value)
                    }else if(item.unit === "%") {
                        value = Decimal(value).times(100).toNumber()
                    }
                    item.value = value
                })
                return v
            })
            setRenderProjectOverview(gaugeData)
        }
    });

    useEffect(() => {
        getProjectOwnerDashboard(projectID, departs);
    }, [projectID, departs, getProjectOwnerDashboard]);

    return (
        <div className="bg-white p-6">
            <div className="mx-auto">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-sm font-bold text-gray-800 mb-2"
                >
                    项目管理数据可视化看板
                </motion.h1>

                <Spin spinning={loading}>
                    <div className="grid grid-cols-4 2xl:grid-cols-8 gap-6 min-h-[100px]">
                        {renderProjectOverview.map((gauge, index) => (
                            <motion.div
                                key={gauge.title}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                                whileHover={{ scale: 1.02 }}
                                className="h-full min-h-[220px]"
                            >
                                <GaugeChart {...gauge} />
                            </motion.div>
                        ))}
                    </div>
                </Spin>
                {!loading && renderProjectOverview.length <= 0 && <div className="text-center text-xs text-gray-500 min-h-[100px]">暂无数据</div>}
            </div>
        </div>
    );
};

// 仪表盘组件
const GaugeChart: React.FC<GaugeProps> = ({
    title,
    isPercent = false,
    subItems,
    color,
    value
}) => {
    const [rotation, setRotation] = useState(0);
    let calculatedPercentage = Decimal(value || 0).times(100).toNumber()
    let anglePercentage = Math.max(0, Math.min(100, calculatedPercentage));
    if (!isPercent) {
        anglePercentage = 50
    }
    const angle = (anglePercentage / 100) * 180; // -90° 到 90°

    // 颜色配置
    const colorConfig = {
        green: {
            gauge: 'text-green-500',
            bg: 'bg-green-50',
            text: 'text-green-700',
            border: 'border-green-200',
        },
        blue: {
            gauge: 'text-blue-500',
            bg: 'bg-blue-50',
            text: 'text-blue-700',
            border: 'border-blue-200',
        },
        yellow: {
            gauge: 'text-yellow-500',
            bg: 'bg-yellow-50',
            text: 'text-yellow-700',
            border: 'border-yellow-200',
        },
        purple: {
            gauge: 'text-purple-500',
            bg: 'bg-purple-50',
            text: 'text-purple-700',
            border: 'border-purple-200',
        },
        orange: {
            gauge: 'text-orange-500',
            bg: 'bg-orange-50',
            text: 'text-orange-700',
            border: 'border-orange-200',
        },
        pink: {
            gauge: 'text-pink-500',
            bg: 'bg-pink-50',
            text: 'text-pink-700',
            border: 'border-pink-200',
        },
    };

    const colors = colorConfig[color];

    useEffect(() => {
        setRotation(angle);
    }, [angle]);

    return (
        <div className={`rounded-xl ${colors.bg} ${colors.border} border p-4 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col`}>
            {/* 标题 */}
            <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>

            {/* <div className="relative flex items-center justify-center flex-1"> */}
                {/* 仪表盘背景 */}
                <div className="relative">
                    {/* 外圆环 */}
                    <svg
                        className="w-full h-full"
                        viewBox="0 0 200 100"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ overflow: 'visible' }}
                    >
                        {/* 仪表盘背景弧线 - 占满SVG上半部分 */}
                        <path
                            d="M 20,90 A 80 80 0 0 1 180,90"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="12"
                            strokeLinecap="round"
                        />

                        {/* 彩色进度弧线 - 动画效果 */}
                        {anglePercentage > 0 && (
                            <motion.path
                                d="M 20,90 A 80 80 0 0 1 180,90"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="12"
                                strokeLinecap="round"
                                className={colors.gauge}
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: anglePercentage / 100 }}
                                transition={{ duration: 1.5, ease: "easeOut" }}
                                style={{
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                }}
                            />
                        )}


                        {/* 指针 */}
                        <motion.g
                            initial={{ rotate: -90 }}
                            animate={{ rotate: rotation }}
                            transition={{ type: "spring", stiffness: 100, damping: 15 }}
                            style={{ originX: 0.9, originY: 0.5 }}
                        >
                            <polygon
                                points="30,90 100,84 100,96"
                                className="fill-gray-800"
                            />
                            <circle
                                cx="100"
                                cy="90"
                                r="6"
                                className="fill-gray-800"
                            />
                        </motion.g>
                        
                    </svg>

                    
                </div>
            {/* </div> */}
            {/* 中间显示数值 */}
            <div className="flex flex-col items-center justify-center mt-2">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: "spring" }}
                    className={`text-2xl font-bold ${isPercent && calculatedPercentage > 100 ? "text-red-500" : "text-gray-800"}`}
                >
                    {isPercent && value ? calculatedPercentage + '%' : value}
                </motion.div>
            </div>

            {/* 子项目列表 */}
            <div className="mt-4 space-y-2">
                {subItems.map((item, index) => (
                    <motion.div
                        key={item.label}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex justify-between text-sm"
                    >
                        <span className="text-gray-600">{item.label}</span>
                        <span className={`font-semibold ${colors.text}`}>
                            {item.value}
                            {item.unit && <span className="text-xs">{item.unit}</span>}
                        </span>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// 进度条组件（备用）
const ProgressBar = ({ percentage, color }: { percentage: number; color: string }) => {
    return (
        <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
                className={`h-2 rounded-full ${color}`}
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 1.5 }}
            />
        </div>
    );
};

export default ProjectManagementDashboard;